import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const AccessibilityContext = createContext(null);

const STORAGE_KEY = 'ifys.accessibility';

const readStoredSettings = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

export function AccessibilityProvider({ children }) {
  const stored = useMemo(readStoredSettings, []);
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [reduceMotion, setReduceMotion] = useState(Boolean(stored?.reduceMotion ?? prefersReducedMotion));
  const [highContrast, setHighContrast] = useState(Boolean(stored?.highContrast ?? false));

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ reduceMotion, highContrast }));
    } catch {
      // Ignore storage failures (e.g. privacy mode).
    }
  }, [reduceMotion, highContrast]);

  useEffect(() => {
    document.documentElement.classList.toggle('reduced-motion', reduceMotion);
  }, [reduceMotion]);

  useEffect(() => {
    document.documentElement.classList.toggle('high-contrast', highContrast);
  }, [highContrast]);

  return (
    <AccessibilityContext.Provider value={{ reduceMotion, setReduceMotion, highContrast, setHighContrast }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error('useAccessibility must be used within AccessibilityProvider');
  return ctx;
}
